import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { of, throwError } from 'rxjs';
import { catchError, debounceTime, map } from 'rxjs/operators';
import { SipConfig, SipConfigCollection } from '../../../shared/models/sip.model';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { ModalService } from '../../../shared/services/modal.service';
import { SipConfigRequestParams, SipService } from '../../../shared/services/sip.service';
import { SuppliersService } from '../../../shared/services/suppliers.service';
import { UsersService } from '../../../shared/services/users.service';

@Component({
  standalone: false,
  selector: 'app-sip-config-select',
  templateUrl: 'sip-config-select.component.html',
  styleUrls: ['sip-config-select.component.scss'],
})
export class SipConfigSelectComponent implements OnDestroy, AfterViewInit {
  @Input() updatePause = false;
  @Input() isFilter = false;

  @Output() onChange = new EventEmitter();

  @ViewChild('elWrapper') elWrapper: ElementRef;
  @ViewChild('scrollViewportSelected') private cdkVirtualScrollViewportSelected: CdkVirtualScrollViewport;
  @ViewChild('scrollViewportAll') private cdkVirtualScrollViewportAll: CdkVirtualScrollViewport;

  models: SipConfig[] = [];
  private modelsIndexes = new Map<number, number>();
  private selectAfterUpdate: SipConfig[] = [];
  requestParams = new SipConfigRequestParams();
  loading = false;
  searchQuery = '';
  totalElements = 0;
  private lastPageLoaded = false;
  searchControl: FormControl = new FormControl();
  updateScheduler;
  schedulerInterval = 30 * 1000;
  rowsVisible = 10;
  private resizeObserver: ResizeObserver | null = null;
  private lastMeasuredWrapperHeight = 0;

  @HostListener('window:resize')
  onResize() {
    if (!this.elWrapper) {
      return;
    }
    const height = this.elWrapper.nativeElement.clientHeight - 30;
    this.updateRowsVisible(height);
  }

  private updateRowsVisible(height: number): void {
    if (!height || height <= 0) {
      this.lastMeasuredWrapperHeight = height || 0;
      return;
    }
    const rows = Math.max(Math.floor(height / 30), 10);
    if (isNaN(rows)) {
      console.error('Destination list Invalid size: ', rows);
      return;
    }
    const rowsChanged = rows !== this.rowsVisible;
    const heightChanged = height !== this.lastMeasuredWrapperHeight;
    this.lastMeasuredWrapperHeight = height;
    if (rowsChanged) {
      this.rowsVisible = rows;
    }
    if (rowsChanged || heightChanged) {
      this.cdkVirtualScrollViewportAll?.checkViewportSize();
    }
  }

  private initializeResizeObserver(): void {
    if (!this.elWrapper || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const height = entry.contentRect && entry.contentRect.height ? entry.contentRect.height - 30 : 0;
        this.updateRowsVisible(height);
      });
    });
    this.resizeObserver.observe(this.elWrapper.nativeElement);
  }

  constructor(
    private sipService: SipService,
    public userService: UsersService,
    public modal: ModalService,
    public localStorage: LocalStorageService
  ) {
    this.modal = modal;
  }

  ngAfterViewInit(): void {
    this.requestParams.size = 100;
    this.searchControl.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.searchQuery = this.searchControl.value.trim();
      this.requestParams.page = 0;
      this.requestParams.search = this.searchQuery;
      this.update(false, false);
    });
    this.onResize();
    this.initializeResizeObserver();
    if (this.isFilter) {
      this.update(false, false);
    }
  }

  getSelectedModels() {
    const selected = new Map<number, SipConfig>();
    this.models
      .filter(_ => _.selected)
      .forEach(s => {
        const key = s.id;
        s.selected = true;
        selected.set(key, s);
      });
    return Array.from(selected.values());
  }

  reset() {
    this.models.map(_ => (_.selected = false));
    this.searchControl.patchValue('');
    this.searchQuery = '';
  }

  private rebuildModelIndexes(models: SipConfig[] = this.models): void {
    this.modelsIndexes = new Map<number, number>();
    models.forEach((c, i) => this.modelsIndexes.set(c.id, i));
  }

  private upsertChunk(chunk: SipConfig[]): void {
    const nextModels = [...this.models];

    chunk.forEach(m => {
      const idx = this.modelsIndexes.get(m.id);
      if (idx !== undefined && idx !== null && nextModels[idx]) {
        const selected = nextModels[idx].selected;
        nextModels[idx] = { ...m, selected };
      } else {
        nextModels.push(m);
      }
    });

    this.models = nextModels;
    this.rebuildModelIndexes();
    this.totalElements = this.models.length;
    this.cdkVirtualScrollViewportAll?.checkViewportSize();
  }

  update(returnObservable = false, loading = true): unknown {
    if (this.updatePause) {
      return returnObservable ? of([]) : null;
    }

    if (this.requestParams.page === 0) {
      this.lastPageLoaded = false;
    }

    if (loading) {
      this.loading = true;
    }

    if (!this.updateScheduler && !this.isFilter) {
      this.updateScheduler = setInterval(() => this.update(false, false), this.schedulerInterval);
    }

    const successHandler = (data: SipConfigCollection) => {
      const orderedChunk = this.getModelsInOrder(data.content);

      if (this.requestParams.page === 0) {
        this.models = [...orderedChunk];
        this.rebuildModelIndexes();
        this.cdkVirtualScrollViewportAll?.checkViewportSize();
        this.totalElements = typeof data.totalElements === 'number' ? data.totalElements : this.models.length;
      } else {
        this.upsertChunk(orderedChunk);
        this.totalElements = typeof data.totalElements === 'number' ? data.totalElements : this.totalElements;
      }

      this.lastPageLoaded =
        (typeof data.totalElements === 'number' && this.models.length >= data.totalElements) || orderedChunk.length < this.requestParams.size;

      if (this.selectAfterUpdate.length) {
        this.models.forEach(m => {
          if (m.id === this.selectAfterUpdate[0].id) {
            m.selected = true;
            this.selectAfterUpdate = [];
          }
        });
      }

      if (loading) {
        this.loading = false;
      }
    };

    const errorHandler = res => {
      if (loading) {
        this.loading = false;
      }
      return throwError(() => new Error(res));
    };
    const updateRequest = this.sipService.allConfig(this.requestParams).pipe(map(successHandler), catchError(errorHandler));
    return returnObservable ? updateRequest : updateRequest.subscribe();
  }

  isSipTrunkDisabled(model: SipConfig): boolean {
    if (model.selected) return false;
    if (this.isFilter) return false;

    return !this.isSipTrunkActive(model);
  }

  isSipTrunkActive(model: SipConfig): boolean {
    return model.online || (!model.pingEnabled && model.enabled);
  }

  getSipTrunkCategoryClass(model: SipConfig): string {
    if (model.online) {
      return 'category-online';
    }
    if (!model.pingEnabled && model.enabled) {
      return 'category-ping-disabled';
    }
    return 'category-offline';
  }

  getSipTrunkTooltipContent(model: SipConfig): string {
    let content = `${model.host}:${model.port}`;
    if (!model.pingEnabled) {
      content += `<p class='mb-0'>OPTIONS pings are disabled</p>`;
    }
    return content;
  }

  toggle(model: SipConfig, value: boolean | null = null, emmit = true) {
    this.selectAfterUpdate = [];
    if (!this.isFilter && !this.isSipTrunkActive(model)) {
      return;
    }
    const mainConn = this.modelsIndexes.has(model.id) ? this.modelsIndexes.get(model.id) : null;
    const selected = value === null ? !model.selected : value;

    if (selected && !this.isFilter) {
      this.models.forEach(_ => (_.selected = false));
    }

    if (mainConn !== null && this.models[mainConn]) {
      this.models[mainConn].selected = selected;
      this.selectAfterUpdate = [this.models[mainConn]];
    }
    if (emmit) {
      this.onChange.emit();
    }
  }

  getModelsInOrder(models: SipConfig[]): SipConfig[] {
    let online: SipConfig[] = [];
    let offline: SipConfig[] = [];
    models.map(m => {
      if (m.online) {
        online.push(m);
      } else {
        offline.push(m);
      }
    });
    // Using collator here for handling very huge sized arrays without performance hit.
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    online = online.sort((a, b) => collator.compare(a.name, b.name));
    offline = offline.sort((a, b) => collator.compare(a.name, b.name));
    return [...online, ...offline];
  }

  getAttributeName(attributeId: string): string {
    return SuppliersService.ATTRIBUTE_TO_LABEL[attributeId];
  }

  onScrolledIndexChange(index: number) {
    if (!index || !this.models.length || this.loading || this.lastPageLoaded) {
      return;
    }
    const currentIndex = index + this.rowsVisible;
    if (currentIndex >= this.models.length) {
      this.requestParams.page++;
      this.update(false, false);
    }
  }

  onDeleteFilterClick() {
    this.reset();
    this.onChange.emit();
  }

  ngOnDestroy() {
    if (this.updateScheduler) {
      clearInterval(this.updateScheduler);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
